import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Empty, Spin, message } from 'antd';
import request from '@/request';
import './index.css';

const SCORE_PAGE_SIZE = 1000;
const SCORE_REFRESH_INTERVAL = 5000;
const CAROUSEL_VIEWPORT_DURATION = 10000;
const CAROUSEL_SCROLL_LOOPS = 3;

const displayValue = (value) => {
  return value === undefined || value === null || value === '' ? '-' : value;
};

const normalizeRound = (value) => {
  if (value === undefined || value === null || value === '') return null;
  return String(value).trim() || null;
};

const getScoreRounds = (records) => {
  return Array.from(
    new Set(records.map((record) => normalizeRound(record.part3)).filter(Boolean))
  ).sort((left, right) => {
    const leftNumber = Number(left);
    const rightNumber = Number(right);

    if (Number.isFinite(leftNumber) && Number.isFinite(rightNumber)) {
      return leftNumber - rightNumber;
    }

    return left.localeCompare(right, 'zh-CN', { numeric: true });
  });
};

const getRoundConfig = (data) => {
  if (!data || typeof data !== 'object') {
    return [];
  }

  const counts = Object.values(data);
  return counts.map((_, index) => String(index + 1));
};

const mergeRounds = (...roundLists) => {
  return getScoreRounds(
    roundLists.flat().map((round) => ({ part3: round }))
  );
};

const hasScoreValue = (record) => {
  return (
    record?.part1 !== undefined &&
    record?.part1 !== null &&
    record?.part1 !== ''
  );
};

const getCarouselRound = (rounds, records) => {
  if (rounds.length <= 1) return rounds[0] || null;

  for (let index = rounds.length - 1; index >= 0; index -= 1) {
    const round = rounds[index];
    const hasScore = records.some(
      (record) =>
        normalizeRound(record.part3) === round && hasScoreValue(record)
    );

    if (hasScore) return round;
  }

  return rounds[0];
};

const getNumericScore = (record) => {
  if (
    record?.part1 === undefined ||
    record?.part1 === null ||
    record?.part1 === ''
  ) {
    return null;
  }

  const score = Number(record.part1);
  return Number.isFinite(score) ? score : null;
};

const getRankedScores = (records) => {
  const sortedRecords = [...records].sort((left, right) => {
    const leftScore = getNumericScore(left);
    const rightScore = getNumericScore(right);

    if (leftScore === null && rightScore === null) return 0;
    if (leftScore === null) return 1;
    if (rightScore === null) return -1;
    return leftScore - rightScore;
  });

  let previousScore = null;
  let previousRank = null;

  return sortedRecords.map((record, index) => {
    const score = getNumericScore(record);

    if (score === null) {
      return { record, calculatedRank: null };
    }

    if (previousScore === null || score !== previousScore) {
      previousScore = score;
      previousRank = index + 1;
    }

    return { record, calculatedRank: previousRank };
  });
};

/** ScoreScreen：独立展示赛事各赛项成绩的大屏页面 */
export default function ScoreScreen() {
  const [searchParams] = useSearchParams();
  const eventId = searchParams.get('eventId');
  const initialItemId = searchParams.get('itemId');
  const [event, setEvent] = useState(null);
  const [items, setItems] = useState([]);
  const [activeItem, setActiveItem] = useState(null);
  const [scores, setScores] = useState([]);
  const [rounds, setRounds] = useState([]);
  const [activeRound, setActiveRound] = useState(null);
  const [eventLoading, setEventLoading] = useState(true);
  const [scoreLoading, setScoreLoading] = useState(false);
  const [eventError, setEventError] = useState('');
  const [scoreError, setScoreError] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [loadedItemId, setLoadedItemId] = useState(null);
  const [isCarouselPlaying, setIsCarouselPlaying] = useState(false);
  const [carouselStep, setCarouselStep] = useState(0);
  const [carouselContentState, setCarouselContentState] = useState({
    key: '',
    hasOverflow: false,
  });
  const carouselButtonRef = useRef(null);
  const tableShellRef = useRef(null);
  const tableBodyRef = useRef(null);
  const itemsRef = useRef(items);
  const activeItemRef = useRef(activeItem);
  const roundsRef = useRef(rounds);
  const scoresRef = useRef(scores);
  const isCarouselPlayingRef = useRef(isCarouselPlaying);

  useEffect(() => {
    itemsRef.current = items;
    activeItemRef.current = activeItem;
    roundsRef.current = rounds;
    scoresRef.current = scores;
    isCarouselPlayingRef.current = isCarouselPlaying;
  }, [
    activeItem,
    isCarouselPlaying,
    items,
    rounds,
    scores,
  ]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };

    handleFullscreenChange();
    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    let active = true;

    if (!eventId) {
      setEventError('缺少赛事信息，无法打开成绩大屏');
      setEventLoading(false);
      return () => {
        active = false;
      };
    }

    setEventLoading(true);
    setEventError('');

    request
      .get(`/events/info/${eventId}`)
      .then((data) => {
        if (!active) return;

        const eventItems = Array.isArray(data?.items) ? data.items : [];
        const initialItem =
          eventItems.find(
            (item) => String(item.id) === String(initialItemId || '')
          ) || eventItems[0] || null;

        setEvent(data);
        setItems(eventItems);
        setActiveItem(initialItem);
      })
      .catch((error) => {
        if (active) {
          setEventError(error.message || '赛事信息加载失败');
        }
      })
      .finally(() => {
        if (active) {
          setEventLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [eventId, initialItemId]);

  useEffect(() => {
    let active = true;
    let refreshTimer = null;
    let requesting = false;
    let knownRounds = [];

    const clearRefreshTimer = () => {
      if (refreshTimer) {
        window.clearTimeout(refreshTimer);
        refreshTimer = null;
      }
    };

    if (!eventId || !activeItem?.id) {
      setScores([]);
      setRounds([]);
      setActiveRound(null);
      setLoadedItemId(null);
      return () => {
        active = false;
      };
    }

    const scheduleNextRefresh = () => {
      clearRefreshTimer();

      if (active && document.visibilityState === 'visible') {
        refreshTimer = window.setTimeout(() => {
          loadScores(false);
        }, SCORE_REFRESH_INTERVAL);
      }
    };

    const loadScores = async (showLoading) => {
      if (!active || requesting) return;

      requesting = true;
      clearRefreshTimer();

      if (showLoading) {
        setScoreLoading(true);
        setScoreError('');
        setScores([]);
        setRounds([]);
        setActiveRound(null);
        setLoadedItemId(null);
      }

      try {
        const [data, roundData] = await Promise.all([
          request.get(
            `/events/admin/import/outer/player/score/list?eventId=${encodeURIComponent(eventId)}&itemId=${encodeURIComponent(activeItem.id)}&page=1&pageSize=${SCORE_PAGE_SIZE}`
          ),
          showLoading
            ? request
                .get(`/events/schedule/round/player/${activeItem.id}`)
                .catch(() => null)
            : Promise.resolve(null),
        ]);

        if (!active) return;

        const scoreItems = Array.isArray(data)
          ? data
          : Array.isArray(data?.items)
            ? data.items
            : [];
        const scoreRounds = getScoreRounds(scoreItems);
        const configuredRounds = getRoundConfig(roundData);
        const nextRounds = showLoading
          ? mergeRounds(configuredRounds, scoreRounds)
          : mergeRounds(knownRounds, scoreRounds);

        knownRounds = nextRounds;

        setScores(scoreItems);
        setRounds(nextRounds);
        setActiveRound((currentRound) => {
          if (showLoading && isCarouselPlayingRef.current) {
            return getCarouselRound(nextRounds, scoreItems);
          }

          return currentRound && nextRounds.includes(currentRound)
            ? currentRound
            : nextRounds[0] || null;
        });
        setLoadedItemId(String(activeItem.id));
        setScoreError('');
      } catch (error) {
        if (active && showLoading) {
          setScores([]);
          setRounds([]);
          setActiveRound(null);
          setScoreError(error.message || '成绩加载失败');
        }
      } finally {
        requesting = false;

        if (active) {
          if (showLoading) {
            setScoreLoading(false);
          }
          scheduleNextRefresh();
        }
      }
    };

    const handleVisibilityChange = () => {
      clearRefreshTimer();

      if (document.visibilityState === 'visible') {
        loadScores(false);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    loadScores(true);

    return () => {
      active = false;
      clearRefreshTimer();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [eventId, activeItem]);

  const stopCarousel = useCallback(() => {
    isCarouselPlayingRef.current = false;
    setIsCarouselPlaying(false);
  }, []);

  useEffect(() => {
    if (!isCarouselPlaying) return undefined;

    const handleUserOperation = (event) => {
      const operatesCarouselButton =
        carouselButtonRef.current?.contains(event.target) &&
        (event.type === 'pointerdown' ||
          (event.type === 'keydown' &&
            (event.key === 'Enter' || event.key === ' ')));

      if (operatesCarouselButton) return;
      stopCarousel();
    };

    document.addEventListener('pointerdown', handleUserOperation, true);
    document.addEventListener('wheel', handleUserOperation, true);
    document.addEventListener('keydown', handleUserOperation, true);

    return () => {
      document.removeEventListener('pointerdown', handleUserOperation, true);
      document.removeEventListener('wheel', handleUserOperation, true);
      document.removeEventListener('keydown', handleUserOperation, true);
    };
  }, [isCarouselPlaying, stopCarousel]);

  const advanceCarousel = useCallback(() => {
    if (!isCarouselPlayingRef.current) return;

    const currentItems = itemsRef.current;
    const currentItem = activeItemRef.current;

    if (!currentItems.length || !currentItem) {
      stopCarousel();
      return;
    }

    const itemIndex = currentItems.findIndex(
      (item) => String(item.id) === String(currentItem.id)
    );
    const nextItem = currentItems[(itemIndex + 1) % currentItems.length];

    if (currentItems.length === 1) {
      setActiveRound(
        getCarouselRound(roundsRef.current, scoresRef.current)
      );
    } else {
      setActiveItem(nextItem);
    }
    setCarouselStep((step) => step + 1);
  }, [stopCarousel]);

  const handleCarouselToggle = () => {
    if (isCarouselPlayingRef.current) {
      stopCarousel();
      return;
    }

    const firstItem = items[0];
    if (!firstItem) return;

    isCarouselPlayingRef.current = true;
    setIsCarouselPlaying(true);
    setActiveItem(firstItem);

    if (String(activeItem?.id) === String(firstItem.id)) {
      setActiveRound(getCarouselRound(rounds, scores));
    }

    setCarouselStep((step) => step + 1);
  };

  const handleFullscreenToggle = async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
        return;
      }

      if (!document.documentElement.requestFullscreen) {
        message.error('当前浏览器不支持全屏展示');
        return;
      }

      await document.documentElement.requestFullscreen();
    } catch (error) {
      message.error(error.message || '全屏切换失败');
    }
  };

  const showGroupName =
    activeItem?.groupName && activeItem.groupName !== activeItem.name;
  const showRoundTabs = rounds.length > 1;
  const displayedScores =
    showRoundTabs && activeRound
      ? scores.filter(
          (record) => normalizeRound(record.part3) === activeRound
        )
      : scores;
  const rankedScores = getRankedScores(displayedScores);
  const carouselContentReady =
    isCarouselPlaying &&
    activeItem?.id &&
    String(loadedItemId) === String(activeItem.id) &&
    !scoreLoading &&
    !scoreError;
  const carouselContentKey = carouselContentReady
    ? `${carouselStep}-${activeItem.id}-${activeRound || 'all'}-${rankedScores.length}`
    : '';
  const duplicateCarouselRows =
    carouselContentReady &&
    carouselContentState.key === carouselContentKey &&
    carouselContentState.hasOverflow;

  useLayoutEffect(() => {
    if (!carouselContentKey) return;

    const tableShell = tableShellRef.current;
    if (tableShell) {
      tableShell.scrollTop = 0;
    }

    const hasOverflow = Boolean(
      tableShell && tableShell.scrollHeight > tableShell.clientHeight + 1
    );

    setCarouselContentState({
      key: carouselContentKey,
      hasOverflow,
    });
  }, [carouselContentKey]);

  useEffect(() => {
    if (
      !carouselContentKey ||
      carouselContentState.key !== carouselContentKey
    ) {
      return undefined;
    }

    let cancelled = false;
    let animationFrame = null;
    let stayTimer = null;

    const finishCurrentRound = () => {
      if (!cancelled) {
        advanceCarousel();
      }
    };

    if (!carouselContentState.hasOverflow) {
      stayTimer = window.setTimeout(
        finishCurrentRound,
        CAROUSEL_VIEWPORT_DURATION
      );
    } else {
      const tableShell = tableShellRef.current;
      const tableBody = tableBodyRef.current;

      if (!tableShell || !tableBody) return undefined;

      tableShell.scrollTop = 0;
      let completedLoops = 0;
      let previousTime = null;

      const scroll = (currentTime) => {
        if (cancelled) return;

        if (previousTime !== null) {
          const elapsed = Math.min(currentTime - previousTime, 100);
          const pixelsPerMillisecond =
            tableShell.clientHeight / CAROUSEL_VIEWPORT_DURATION;
          const loopHeight = tableBody.scrollHeight;

          tableShell.scrollTop += elapsed * pixelsPerMillisecond;

          if (loopHeight > 0 && tableShell.scrollTop >= loopHeight) {
            tableShell.scrollTop -= loopHeight;
            completedLoops += 1;

            if (completedLoops >= CAROUSEL_SCROLL_LOOPS) {
              tableShell.scrollTop = 0;
              finishCurrentRound();
              return;
            }
          }
        }

        previousTime = currentTime;
        animationFrame = window.requestAnimationFrame(scroll);
      };

      animationFrame = window.requestAnimationFrame(scroll);
    }

    return () => {
      cancelled = true;
      if (animationFrame) {
        window.cancelAnimationFrame(animationFrame);
      }
      if (stayTimer) {
        window.clearTimeout(stayTimer);
      }
    };
  }, [advanceCarousel, carouselContentKey, carouselContentState]);

  const renderScoreRows = (keyPrefix = '') => {
    return rankedScores.map(({ record, calculatedRank }, index) => {
      const hasScore = getNumericScore(record) !== null;
      const topRank = [1, 2, 3].includes(calculatedRank)
        ? calculatedRank
        : null;

      return (
        <tr key={`${keyPrefix}${record.id ?? record.playerId ?? index}`}>
          <td>
            <span
              className={`score-screen__rank${
                topRank ? ` score-screen__rank--${topRank}` : ''
              }`}
            >
              {calculatedRank ?? '-'}
            </span>
          </td>
          <td className="score-screen__player-name">
            {displayValue(record.name)}
          </td>
          <td className="score-screen__player-id">
            {displayValue(record.playerId)}
          </td>
          <td>
            <span
              className={
                hasScore
                  ? 'score-screen__score'
                  : 'score-screen__score score-screen__score--empty'
              }
            >
              {displayValue(record.part1)}
            </span>
          </td>
        </tr>
      );
    });
  };

  if (eventLoading) {
    return (
      <div className="score-screen-state">
        <Spin size="large" tip="正在加载赛事信息" />
      </div>
    );
  }

  if (eventError) {
    return (
      <div className="score-screen-state">
        <div className="score-screen-state__error">{eventError}</div>
      </div>
    );
  }

  return (
    <div className="score-screen">
      <aside className="score-screen__sidebar">
        <div className="score-screen__brand">
          <p className="score-screen__eyebrow">赛事成绩大屏</p>
          <h1 className="score-screen__event-name">
            {displayValue(event?.name)}
          </h1>
          <p className="score-screen__event-meta">共 {items.length} 个赛项</p>
        </div>

        <nav className="score-screen__nav">
          <p className="score-screen__nav-title">赛项列表</p>
          <div className="score-screen__nav-list">
            {items.map((item, index) => {
              const selected = activeItem?.id === item.id;

              return (
                <button
                  key={item.id}
                  type="button"
                  aria-current={selected ? 'true' : undefined}
                  onClick={() => setActiveItem(item)}
                  className={`score-screen__nav-item${
                    selected ? ' score-screen__nav-item--active' : ''
                  }`}
                >
                  <span className="score-screen__nav-index">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <span className="score-screen__nav-content">
                    <span className="score-screen__nav-name">
                      {displayValue(item.name)}
                    </span>
                    {item.groupName && item.groupName !== item.name && (
                      <span className="score-screen__nav-group">
                        {item.groupName}
                      </span>
                    )}
                  </span>
                  <span className="score-screen__nav-arrow" aria-hidden="true">
                    ›
                  </span>
                </button>
              );
            })}
          </div>
        </nav>
      </aside>

      <main className="score-screen__main">
        <header className="score-screen__header">
          <div className="score-screen__heading">
            <p className="score-screen__section-label">当前赛项</p>
            <h2 className="score-screen__item-name">
              {displayValue(activeItem?.name)}
            </h2>
            {showGroupName && (
              <p className="score-screen__group-name">
                {activeItem.groupName}
              </p>
            )}
          </div>

          <div className="score-screen__header-actions">
            <div className="score-screen__player-count">
              <strong>{scoreLoading ? '--' : displayedScores.length}</strong>
              <span>名选手</span>
            </div>
            <button
              ref={carouselButtonRef}
              type="button"
              className={`score-screen__carousel-button${
                isCarouselPlaying
                  ? ' score-screen__carousel-button--active'
                  : ''
              }`}
              aria-label={isCarouselPlaying ? '停止轮播' : '开始轮播'}
              aria-pressed={isCarouselPlaying}
              title={isCarouselPlaying ? '停止轮播' : '开始轮播'}
              disabled={items.length === 0}
              onClick={handleCarouselToggle}
            >
              {isCarouselPlaying ? (
                <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M7 5h3v14H7z" />
                  <path d="M14 5h3v14h-3z" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M8 5v14l11-7z" />
                </svg>
              )}
            </button>
            <button
              type="button"
              className={`score-screen__fullscreen-button${
                isFullscreen
                  ? ' score-screen__fullscreen-button--active'
                  : ''
              }`}
              aria-label={isFullscreen ? '退出全屏' : '全屏展示'}
              title={isFullscreen ? '退出全屏' : '全屏展示'}
              onClick={handleFullscreenToggle}
            >
              {isFullscreen ? (
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M9 3v4a2 2 0 0 1-2 2H3" />
                  <path d="M15 3v4a2 2 0 0 0 2 2h4" />
                  <path d="M9 21v-4a2 2 0 0 0-2-2H3" />
                  <path d="M15 21v-4a2 2 0 0 1 2-2h4" />
                </svg>
              ) : (
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M8 3H5a2 2 0 0 0-2 2v3" />
                  <path d="M16 3h3a2 2 0 0 1 2 2v3" />
                  <path d="M8 21H5a2 2 0 0 1-2-2v-3" />
                  <path d="M16 21h3a2 2 0 0 0 2-2v-3" />
                </svg>
              )}
            </button>
          </div>
        </header>

        <section
          className={`score-screen__content${
            showRoundTabs ? ' score-screen__content--with-rounds' : ''
          }`}
        >
          {showRoundTabs && (
            <div
              className="score-screen__round-tabs"
              role="tablist"
              aria-label="成绩轮次"
            >
              {rounds.map((round) => (
                <button
                  key={round}
                  type="button"
                  role="tab"
                  aria-selected={activeRound === round}
                  className={`score-screen__round-tab${
                    activeRound === round
                      ? ' score-screen__round-tab--active'
                      : ''
                  }`}
                  onClick={() => setActiveRound(round)}
                >
                  第{round}轮
                </button>
              ))}
            </div>
          )}

          {scoreError ? (
            <div className="score-screen__content-state score-screen__content-state--error">
              {scoreError}
            </div>
          ) : scoreLoading ? (
            <div className="score-screen__content-state">
              <Spin size="large" tip="正在加载成绩" />
            </div>
          ) : displayedScores.length === 0 ? (
            <div className="score-screen__content-state">
              <Empty description="暂无成绩" />
            </div>
          ) : (
            <div ref={tableShellRef} className="score-screen__table-shell">
              <table className="score-screen__table">
                <thead>
                  <tr>
                    <th>排名</th>
                    <th>选手</th>
                    <th>选手编号</th>
                    <th>成绩</th>
                  </tr>
                </thead>
                <tbody ref={tableBodyRef}>{renderScoreRows()}</tbody>
                {duplicateCarouselRows && (
                  <tbody aria-hidden="true">
                    {renderScoreRows('carousel-copy-')}
                  </tbody>
                )}
              </table>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
